import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Chip, Typography, Box, TableSortLabel, IconButton, Tooltip } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PriceUpdatedBadge from "./PriceUpdatedBadge";
import { priceChangesFor } from "../../utils/subscriptionPriceChanges";

export const REVENUE_HINT = "Customer price, GST included. Admin fee is not deducted.";
export const COST_HINT = "Merchant payout, GST included, after admin fee.";
export const PROFIT_HINT = "GST-inclusive customer price minus GST-inclusive merchant payout.";

function FormulaTip({ label, formula }) {
  const [hovering, setHovering] = useState(false);
  const [pinned, setPinned] = useState(false);
  return (
    <Tooltip
      title={formula}
      open={hovering || pinned}
      disableHoverListener
      disableFocusListener
      disableTouchListener
      componentsProps={{ tooltip: { sx: { maxWidth: 280 } } }}
    >
      <IconButton
        aria-label={`${label} formula`}
        size="small"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          setPinned((current) => !current);
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  );
}

const ORANGE_CELL = { bgcolor: "rgba(237, 108, 2, 0.22)" };

function formatDate(dateString) {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "AUD" });
}

function formatNumber(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
}

function formatPendingRate() {
  return (
    <Box>
      <Typography variant="body2">—</Typography>
      <Typography variant="caption" color="text.secondary">
        after 1 month
      </Typography>
    </Box>
  );
}

function formatRate(value) {
  if (value == null || value === "") return formatPendingRate();
  const n = Number(value);
  if (!Number.isFinite(n)) return formatPendingRate();
  return (
    <Box>
      <Typography variant="body2">{n.toFixed(2)}</Typography>
      <Typography variant="caption" color="text.secondary">
        washes / month
      </Typography>
    </Box>
  );
}

function formatMonthlyProfit(value) {
  if (value == null || value === "") return formatPendingRate();
  const n = Number(value);
  if (!Number.isFinite(n)) return formatPendingRate();
  return formatMoney(n);
}

const GENERIC_UTM = new Set(["", "stripe", "null", "undefined"]);

function displayUtm(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed || GENERIC_UTM.has(trimmed.toLowerCase())) return null;
  return trimmed;
}

function SourceCell({ row }) {
  const utmSource = displayUtm(row.sourceRaw);
  const utmMedium = displayUtm(row.mediumRaw);
  const utmCampaign = displayUtm(row.campaignRaw);
  const utmContent = displayUtm(row.contentRaw);
  const hasUtm = utmSource || utmMedium || utmCampaign || utmContent;

  return (
    <Box sx={{ minWidth: 180 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {row.leadSource || "unknown"}
      </Typography>
      {row.sourceReason && (
        <Typography variant="caption" display="block" color="text.secondary">
          reason: {row.sourceReason}
        </Typography>
      )}
      {hasUtm && (
        <Box sx={{ mt: 0.5, color: "text.secondary" }}>
          {utmSource && (
            <Typography variant="caption" display="block">
              source: {utmSource}
            </Typography>
          )}
          {utmMedium && (
            <Typography variant="caption" display="block">
              medium: {utmMedium}
            </Typography>
          )}
          {utmCampaign && (
            <Typography variant="caption" display="block">
              campaign: {utmCampaign}
            </Typography>
          )}
          {utmContent && (
            <Typography variant="caption" display="block">
              content: {utmContent}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

const statusColor = {
  active: "success",
  inactive: "warning",
  cancelled: "default",
};

export const ANALYTICS_COLUMNS = [
  { id: "view", label: "View", sortable: false },
  { id: "email", label: "Email" },
  { id: "rego", label: "Rego" },
  { id: "productName", label: "Product" },
  { id: "leadSource", label: "Source" },
  {
    id: "startDateUTC",
    label: "Start date",
    formula: "This subscription's own start date (or valid from, then created). First wash or payment only if none is set",
  },
  {
    id: "lastPaymentDateUTC",
    label: "Last payment",
    formula: "Date of the latest successful charge",
  },
  {
    id: "paymentStatus",
    label: "Subscription status",
    formula: "Active while still accruing. Cancelled if deleted or past valid-until. Otherwise Inactive",
  },
  {
    id: "accountActivationStatus",
    label: "Account activation",
    formula: "Activated after invite completion, or if the account was created already active",
  },
  {
    id: "monthsActive",
    label: "Months active",
    formula: "Days from start to activity end ÷ 30.437. End is today, or valid-until once the membership has stopped",
  },
  {
    id: "monthlyRedemptionRate",
    label: "Redemption rate",
    formula: "Washes in the active period ÷ months active. Blank under 1 month",
  },
  {
    id: "totalRedemptions",
    label: "Total redemptions",
    formula: "Washes on or before the activity end",
  },
  {
    id: "monthlyRevenue",
    label: "Monthly revenue",
    formula: `Latest paid charge on a monthly billing cycle. If there is no monthly charge, a 12-month product price is the yearly amount ÷ 12. ${REVENUE_HINT}`,
  },
  {
    id: "annualRevenue",
    label: "Annual revenue",
    formula: `Monthly revenue × 12. A multi-year price is one year's share of the full term, not the whole contract multiplied by 12. ${REVENUE_HINT}`,
  },
  {
    id: "monthlyRedemptionCost",
    label: "Redemption cost per month",
    formula: `Total wash payout ÷ months active. Payout is the Xero bill total, or the contract rate after admin fee. Blank under 1 month. ${COST_HINT}`,
  },
  {
    id: "annualRedemptionCost",
    label: "Annual redemption cost",
    formula: `Redemption cost per month × 12. ${COST_HINT}`,
  },
  {
    id: "monthlyProfit",
    label: "Monthly profit",
    formula:
      "(Membership revenue earned so far − total amount paid to dealers) ÷ months active. Each charge counts across the month or term it pays for, so a payment taken in advance only counts for the days already used. Blank under 1 month. " +
      PROFIT_HINT,
  },
  {
    id: "annualProfit",
    label: "Annual profit",
    formula: `Monthly profit × 12. ${PROFIT_HINT}`,
  },
  {
    id: "profitLast30Days",
    label: "Profit last 30 days",
    formula:
      "Membership revenue earned in the last 30 days (each charge spread over the period it pays for) − amount paid to dealers for redemptions in those 30 days. " +
      PROFIT_HINT,
  },
  {
    id: "profitLast60Days",
    label: "Profit last 60 days",
    formula:
      "Membership revenue earned in the last 60 days (each charge spread over the period it pays for) − amount paid to dealers for redemptions in those 60 days. " +
      PROFIT_HINT,
  },
  { id: "nextBillingDateUTC", label: "Next billing" },
];

const activationColor = {
  activated: "success",
  not_activated: "warning",
};

function isNegativeProfit(column, row) {
  const profitColumns = ["monthlyProfit", "annualProfit", "profitLast30Days", "profitLast60Days"];
  return (
    profitColumns.includes(column.id) &&
    row[column.id] != null &&
    row[column.id] !== "" &&
    Number(row[column.id]) < 0
  );
}

function activationLabel(value) {
  if (value === "activated") return "Activated";
  if (value === "not_activated") return "Not activated";
  return value || "unknown";
}

const MembershipAnalyticsTable = ({
  rows,
  compact = false,
  sortBy = "startDateUTC",
  sortDir = "desc",
  onSort,
  priceChanges,
}) => {
  const navigate = useNavigate();

  const handleSort = (field) => {
    if (!onSort) return;
    const nextDir = sortBy === field && sortDir === "asc" ? "desc" : "asc";
    onSort(field, nextDir);
  };

  const renderCell = (column, row, subId) => {
    if (column.id === "view") {
      return (
        <IconButton
          aria-label="View membership"
          size="small"
          disabled={!subId}
          onClick={(event) => {
            event.stopPropagation();
            if (subId) navigate(`/subscription/${subId}`);
          }}
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
      );
    }
    if (column.id === "email") return row.email || "N/A";
    if (column.id === "rego") return row.rego || "N/A";
    if (column.id === "productName") {
      const changes = priceChangesFor(priceChanges, subId);
      if (!changes.length) return row.productName || "N/A";
      return (
        <Box>
          <Typography variant="body2">{row.productName || "N/A"}</Typography>
          <PriceUpdatedBadge changes={changes} />
        </Box>
      );
    }
    if (column.id === "leadSource") return <SourceCell row={row} />;
    if (column.id === "startDateUTC") return formatDate(row.startDateUTC);
    if (column.id === "lastPaymentDateUTC") return formatDate(row.lastPaymentDateUTC);
    if (column.id === "paymentStatus") {
      return (
        <Chip
          size="small"
          label={row.paymentStatus || "unknown"}
          color={statusColor[row.paymentStatus] || "default"}
        />
      );
    }
    if (column.id === "accountActivationStatus") {
      return (
        <Chip
          size="small"
          label={activationLabel(row.accountActivationStatus)}
          color={activationColor[row.accountActivationStatus] || "default"}
        />
      );
    }
    if (column.id === "monthsActive") return formatNumber(row.monthsActive);
    if (column.id === "monthlyRedemptionRate") return formatRate(row.monthlyRedemptionRate);
    if (column.id === "totalRedemptions") return row.totalRedemptions ?? 0;
    if (column.id === "monthlyRevenue") return formatMoney(row.monthlyRevenue);
    if (column.id === "annualRevenue") return formatMoney(row.annualRevenue);
    if (column.id === "monthlyRedemptionCost") return formatMonthlyProfit(row.monthlyRedemptionCost);
    if (column.id === "annualRedemptionCost") return formatMonthlyProfit(row.annualRedemptionCost);
    if (column.id === "monthlyProfit") return formatMonthlyProfit(row.monthlyProfit);
    if (column.id === "annualProfit") return formatMonthlyProfit(row.annualProfit);
    if (column.id === "profitLast30Days") return formatMoney(row.profitLast30Days);
    if (column.id === "profitLast60Days") return formatMoney(row.profitLast60Days);
    if (column.id === "nextBillingDateUTC") return formatDate(row.nextBillingDateUTC);
    return null;
  };

  const cellSx = (column, row) => {
    if (column.id === "monthlyRedemptionRate" && row.monthlyRedemptionRate != null && Number(row.monthlyRedemptionRate) > 1.5) {
      return ORANGE_CELL;
    }
    if (isNegativeProfit(column, row)) return ORANGE_CELL;
    return undefined;
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: compact ? "100%" : "fit-content",
        maxWidth: compact ? "100%" : "calc(100vw - 200px)",
        maxHeight: "calc(100vh - 96px)",
        margin: "0 auto",
        overflow: "auto",
      }}
    >
      <Table
        stickyHeader
        size={compact ? "small" : "medium"}
        sx={{ minWidth: 1600, borderCollapse: "separate", userSelect: "text" }}
        aria-label="membership analytics table"
      >
        <TableHead>
          <TableRow>
            {ANALYTICS_COLUMNS.map((col) => (
              <TableCell
                key={col.id}
                sx={{
                  fontWeight: "bold",
                  position: "sticky",
                  top: 0,
                  zIndex: 3,
                  backgroundColor: "#fff",
                  boxShadow: "inset 0 -1px 0 #e0e0e0",
                }}
                sortDirection={sortBy === col.id ? sortDir : false}
              >
                <Box sx={{ display: "inline-flex", alignItems: "center" }}>
                  {onSort && col.sortable !== false ? (
                    <TableSortLabel
                      active={sortBy === col.id}
                      direction={sortBy === col.id ? sortDir : "asc"}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                  {col.formula ? <FormulaTip label={col.label} formula={col.formula} /> : null}
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {(rows || []).map((row) => {
            const subId = row.subscription?._id || row.subscription;
            return (
              <TableRow key={String(subId || row.email + row.rego)} hover>
                {ANALYTICS_COLUMNS.map((column) => {
                  const highlight =
                    (column.id === "monthlyRedemptionRate" &&
                      row.monthlyRedemptionRate != null &&
                      Number(row.monthlyRedemptionRate) > 1.5) ||
                    isNegativeProfit(column, row);
                  return (
                    <TableCell
                      key={column.id}
                      data-highlight={highlight ? "orange" : undefined}
                      sx={cellSx(column, row)}
                    >
                      {renderCell(column, row, subId)}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MembershipAnalyticsTable;
